import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse, PaginatedApiResponse } from '@card-software/nexus-utilities';
import { InventoryTransaction } from '@app/models/knitting/kntiting-requistion-report';
import { Roll } from '@app/models/knitting/roll';
import { ProductionCardBalanceDto } from '@app/models/production/balance-dto';
import { CustomerPurchaseOrderCuadrajeDto } from '@app/models/production/cuadraje-dto';
import { WorkOrderQCSummaryDto } from '@app/models/production/qc-summary';
import { getSkipperErrorHeader } from '@app/utilities/error-header.util';
import { environment } from '../../../environments/environment';
import {
  ProductionCard,
  ProductionCardClosePreview,
  ProductionCardSearchCommand,
  ProductionCardTransferCheck,
  ProductionCardTransferCheckResult,
} from '../../models/production/production-card';

@Injectable({
  providedIn: 'root',
})
export class ProductionCardsProxyService {
  private readonly httpClient = inject(HttpClient);
  private _baseUrl = `${environment.proxyUrls.productionApi}/api/v1/production-card`;

  public getProductionCards(): Observable<ApiResponse<ProductionCard[]>> {
    return this.httpClient.get<ApiResponse<ProductionCard[]>>(`${this._baseUrl}/all`);
  }

  public getGetProductsCardByIDOrder(id: string): Observable<ApiResponse<ProductionCard[]>> {
    return this.httpClient.get<ApiResponse<ProductionCard[]>>(`${this._baseUrl}/by-production-order/${id}`);
  }
  public getProductionCardByID(id: string): Observable<ApiResponse<ProductionCard>> {
    return this.httpClient.get<ApiResponse<ProductionCard>>(`${this._baseUrl}/${id}`);
  }

  public createProductionCard(productionCard: ProductionCard): Observable<ApiResponse<ProductionCard>> {
    return this.httpClient.post<ApiResponse<ProductionCard>>(`${this._baseUrl}`, productionCard);
  }
  public updateTagsProductionCardByID(id: string, tags: string[]): Observable<ApiResponse<ProductionCard>> {
    return this.httpClient.patch<ApiResponse<ProductionCard>>(`${this._baseUrl}/${id}/tags`, tags);
  }

  public createProductionCardBatch(productionCard: ProductionCard[]): Observable<ApiResponse<ProductionCard>> {
    return this.httpClient.post<ApiResponse<ProductionCard>>(`${this._baseUrl}/batch`, productionCard);
  }

  public updateProductionCardByID(productionCard: ProductionCard): Observable<ApiResponse<ProductionCard>> {
    return this.httpClient.put<ApiResponse<ProductionCard>>(`${this._baseUrl}/${productionCard.id}`, productionCard);
  }

  public deleteProductionCardByID(id: string): Observable<ApiResponse<ProductionCard>> {
    return this.httpClient.delete<ApiResponse<ProductionCard>>(`${this._baseUrl}/${id}`);
  }

  public getProductionCardsByWorkOrderId(workOrderReference: string, avoidInterceptor = false): Observable<ApiResponse<ProductionCard[]>> {
    const headers = getSkipperErrorHeader(avoidInterceptor);
    return this.httpClient.get<ApiResponse<ProductionCard[]>>(`${this._baseUrl}/work-order/${workOrderReference}`, { headers });
  }

  public getProductionCardsByWorkOrderIdList(workOrderReference: string): Observable<ApiResponse<ProductionCard[]>> {
    return this.httpClient.get<ApiResponse<ProductionCard[]>>(`${this._baseUrl}/work-order/${workOrderReference}/list`);
  }

  public getProductionCardsExcludedKnitting(workOrderReference: string): Observable<ApiResponse<ProductionCard[]>> {
    const params = new HttpParams().set('excludeRelatedToKnittingOrder', 'true');
    return this.httpClient.get<ApiResponse<ProductionCard[]>>(`${this._baseUrl}/work-order/${workOrderReference}`, {
      params,
    });
  }

  public getProductionCardByWorkOrderReferences(workOrderReferences: string[]): Observable<ApiResponse<Record<string, ProductionCard[]>>> {
    return this.httpClient.post<ApiResponse<Record<string, ProductionCard[]>>>(`${this._baseUrl}/work-orders`, workOrderReferences);
  }

  public search(
    searchCommand: ProductionCardSearchCommand,
    pageNumber?: number,
    pageSize?: number,
  ): Observable<ApiResponse<ProductionCard[]>> {
    let params = new HttpParams();
    if (pageNumber != null) params = params.set('pageNumber', pageNumber.toString());
    if (pageSize != null) params = params.set('pageSize', pageSize.toString());
    return this.httpClient.post<ApiResponse<ProductionCard[]>>(`${this._baseUrl}/search`, searchCommand, { params });
  }

  public searchPaginated(
    searchCommand: ProductionCardSearchCommand,
    pageNumber = 1,
    pageSize = 50,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PaginatedApiResponse<ProductionCard>>> {
    let params = new HttpParams().set('pageNumber', pageNumber.toString()).set('pageSize', pageSize.toString());
    if (sortField) {
      params = params.set('sortField', sortField).set('sortOrder', sortOrder.toString());
    }
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<ProductionCard>>>(`${this._baseUrl}/search/paginated`, searchCommand, {
      params,
    });
  }

  public assignRollsToProductionCard(productionCardId: string, rolls: string[]): Observable<ApiResponse<ProductionCard>> {
    return this.httpClient.post<ApiResponse<ProductionCard>>(`${this._baseUrl}/${productionCardId}/upsert-roll-numbers`, rolls);
  }

  //#region Important Methods

  public getByProductionCardNumber(productionCardNumber: string): Observable<ApiResponse<ProductionCard>> {
    return this.httpClient.get<ApiResponse<ProductionCard>>(`${this._baseUrl}/number/${encodeURIComponent(productionCardNumber)}`);
  }

  public addRollToInventoryCandidates(
    productionCardReference: string,
    rollNumber: string,
    targetFabricCode?: string,
  ): Observable<ApiResponse<InventoryTransaction>> {
    return this.httpClient.post<ApiResponse<InventoryTransaction>>(`${this._baseUrl}/add-rolls-inventory-candidates`, {
      rollNumber: rollNumber,
      productionCardReference: productionCardReference,
      targetFabricCode: targetFabricCode,
    });
  }

  public batchAddRollsToInventoryCandidates(
    productionCardReference: string,
    rollNumbers: string[],
    isAdding: boolean,
    targetFabricCode?: string,
  ): Observable<ApiResponse<InventoryTransaction>> {
    return this.httpClient.post<ApiResponse<InventoryTransaction>>(
      `${this._baseUrl}/batch/add-rolls-inventory-candidates?isAdding=${isAdding}`,
      {
        rollNumbers: rollNumbers,
        productionCardReference: productionCardReference,
        targetFabricCode: targetFabricCode,
      },
    );
  }

  public changeStatus(productionCardId: string, newStatus: number): Observable<ApiResponse<null>> {
    return this.httpClient.patch<ApiResponse<null>>(`${this._baseUrl}/${productionCardId}/status`, { newStatus: newStatus });
  }

  public getClosePreview(productionCardId: string): Observable<ApiResponse<ProductionCardClosePreview>> {
    return this.httpClient.get<ApiResponse<ProductionCardClosePreview>>(`${this._baseUrl}/${productionCardId}/close-preview`);
  }

  public getActiveInventoryTransactionsInBatching(
    productionCardReference: string,
    avoidInterceptor = false,
  ): Observable<ApiResponse<InventoryTransaction[]>> {
    const headers = getSkipperErrorHeader(avoidInterceptor);
    return this.httpClient.get<ApiResponse<InventoryTransaction[]>>(
      `${this._baseUrl}/${productionCardReference}/inventory-transaction/batching`,
      { headers },
    );
  }

  public getInventoryTransactionsBatchingToGreige(
    productionCardReference: string,
    avoidInterceptor = false,
  ): Observable<ApiResponse<InventoryTransaction[]>> {
    const headers = getSkipperErrorHeader(avoidInterceptor);
    return this.httpClient.get<ApiResponse<InventoryTransaction[]>>(
      `${this._baseUrl}/${productionCardReference}/inventory-transaction/batching-to-greige`,
      { headers },
    );
  }

  public returnRollsToGreige(productionCardReference: string, rollNumbers: string[]): Observable<ApiResponse<Roll[]>> {
    return this.httpClient.post<ApiResponse<Roll[]>>(`${this._baseUrl}/return-rolls-to-greige`, {
      productionCardReference,
      rollNumbers,
    });
  }

  public reAddRollsFromGreigeToBatching(productionCardReference: string, rollNumbers: string[]): Observable<ApiResponse<Roll[]>> {
    return this.httpClient.post<ApiResponse<Roll[]>>(`${this._baseUrl}/re-add-from-greige-to-batching`, {
      productionCardReference,
      rollNumbers,
    });
  }

  public getChildrenRolls(productionCardId: string, locationCode: string): Observable<ApiResponse<Roll[]>> {
    {
      const params = new HttpParams().set('locationCode', locationCode);
      return this.httpClient.get<ApiResponse<Roll[]>>(`${this._baseUrl}/${productionCardId}/children-rolls`, { params });
    }
  }

  //#endregion

  //#region Change Dyeing Machine

  public changeDyeingMachine(productionCardId: string, newDyeingAssetId: string): Observable<ApiResponse<ProductionCard>> {
    return this.httpClient.patch<ApiResponse<ProductionCard>>(
      `${this._baseUrl}/${productionCardId}/dyeing-machine?newDyeingAssetId=${newDyeingAssetId}`,
      {},
    );
  }

  //#endregion

  //#region Finished Goods Methods

  public getFinishedGoodsCandidateRolls(productionCardId: string): Observable<ApiResponse<Roll[]>> {
    return this.httpClient.get<ApiResponse<Roll[]>>(`${this._baseUrl}/${productionCardId}/finished-good-candidate-rolls`);
  }

  public getActiveInventoryTransactionInFinishedGoods(
    productionCardId: string,
    avoidInterceptor = false,
  ): Observable<ApiResponse<InventoryTransaction>> {
    const headers = getSkipperErrorHeader(avoidInterceptor);
    return this.httpClient.get<ApiResponse<InventoryTransaction>>(`${this._baseUrl}/${productionCardId}/finishing-transaction`, {
      headers,
    });
  }

  public addRollToFinishedGoodsCandidates(productionCardId: string, rollNumber: string): Observable<ApiResponse<InventoryTransaction>> {
    return this.httpClient.post<ApiResponse<InventoryTransaction>>(
      `${this._baseUrl}/${productionCardId}/finishing-transaction/add-roll/${encodeURIComponent(rollNumber)}`,
      {},
    );
  }

  public removeRollFromFinishedGoodsCandidates(
    productionCardId: string,
    rollNumber: string,
  ): Observable<ApiResponse<InventoryTransaction>> {
    return this.httpClient.delete<ApiResponse<InventoryTransaction>>(
      `${this._baseUrl}/${productionCardId}/finishing-transaction/remove-roll/${encodeURIComponent(rollNumber)}`,
    );
  }
  //#endregion

  //#region Transfer Check

  public checkTransfer(command: ProductionCardTransferCheck): Observable<ApiResponse<ProductionCardTransferCheckResult>> {
    return this.httpClient.post<ApiResponse<ProductionCardTransferCheckResult>>(`${this._baseUrl}/transfer-check`, command);
  }

  //#endregion

  //#region Work Order QC Summary

  public getWorkOrderQCSummary(workOrderReference: string): Observable<ApiResponse<WorkOrderQCSummaryDto>> {
    return this.httpClient.get<ApiResponse<WorkOrderQCSummaryDto>>(`${this._baseUrl}/work-order/${workOrderReference}/qc-summary`);
  }

  //#endregion

  //#region Cuadraje y Balanceo de POs

  public getCustomerPoCuadraje(productOrderNumber: string): Observable<ApiResponse<CustomerPurchaseOrderCuadrajeDto>> {
    // Query string, not a path segment: Customer POs carry "#" and "/" (e.g. "F#26KONW061", "TARGET C126/2").
    return this.httpClient.get<ApiResponse<CustomerPurchaseOrderCuadrajeDto>>(`${this._baseUrl}/customer-po/cuadraje`, {
      params: new HttpParams().set('productOrderNumber', productOrderNumber),
    });
  }

  public getProductionCardBalance(productionCardId: string): Observable<ApiResponse<ProductionCardBalanceDto>> {
    return this.httpClient.get<ApiResponse<ProductionCardBalanceDto>>(`${this._baseUrl}/${productionCardId}/balance`);
  }

  //#endregion
}
